// =============================================================================
// numericInput — onChange helpers for "type='text' + inputMode='numeric'" inputs
//
// Why this exists (in one sentence): `<input type="number">` silently changes
// its value when the mouse wheel scrolls over a focused input, and on desktop
// it also shows spinner buttons that can be tapped accidentally. Both fire
// without any visual cue. The pattern we use across the app is:
//
//   <input
//     type="text"
//     inputMode="decimal"            (or "numeric" for integer-only)
//     pattern="[0-9]*\.?[0-9]*"      (or "[0-9]*")
//     value={x}
//     onChange={e => setX(safeDecimal(e.target.value))}
//   />
//
// Mobile still surfaces the numeric keypad; desktop loses the spinner +
// wheel-scroll trap. The onChange strip means whatever's in the input box
// is always exactly what's stored in state.
//
// KF49 hit this in production 2026-05-13 — operators logging rejects entered
// "17 bins" but the row sometimes saved 16. Cause: scrolling the page after
// the input was focused stepped the value down by 1. Fix: this helper.
// =============================================================================

/**
 * Strip everything except digits. Use for whole-number inputs
 * (year, carton count, bin count, crew count, etc).
 */
export function safeDigits(v: string): string {
  return v.replace(/[^0-9]/g, '')
}

/**
 * Strip everything except digits + a single decimal point. Use for decimal
 * inputs (kg, hours, hectares, currency, etc). Collapses multiple dots so
 * "12.34.56" → "12.3456". A comma counts as a decimal point: the SA-locale
 * mobile decimal pad (inputMode="decimal", en-ZA/af-ZA) has NO period key —
 * comma is the only separator it offers — so stripping it didn't just block
 * decimals, it silently 10×'d the entry ("16,5" → "165"). KF49 QC box weight,
 * 2026-07-02.
 */
export function safeDecimal(v: string): string {
  // Comma → dot FIRST (SA keypads emit comma, see above), then strip
  // non-numeric and keep only the FIRST dot. The old single-pass /(\..*)\./
  // regex removed only one extra dot per call, so pasting "1.2.3.4"
  // left "1.2.34" — still two points, which corrupts the money/qty field.
  const cleaned = v.replace(/,/g, '.').replace(/[^0-9.]/g, '')
  const dot = cleaned.indexOf('.')
  return dot === -1 ? cleaned : cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '')
}

/**
 * Keystroke filter for the Estimates tons and hectares boxes (U28 D-8): keeps
 * what was typed, only dropping characters that are not a digit, comma or
 * point. Unlike safeDecimal it does NOT turn a comma into a point, so
 * "1,200" stays "1,200" until parseTonsInput reads it on blur / save.
 */
export function keepTypedDecimal(v: string): string {
  return v.replace(/[^0-9.,]/g, '')
}

export type TonsParse =
  | { ok: true; value: number | null }
  | { ok: false; kind: 'thousands' | 'separators' | 'nan'; message: string }

/**
 * Read a typed tons / hectares string. "12,5" and "12.5" are 12.5 (SA comma
 * decimal). Digits, ONE comma and exactly three digits ("1,200") is refused:
 * it is a thousands mark or a decimal, and a guess here silently saved 1.2 t.
 * More than one separator is refused. Blank is { ok: true, value: null }.
 * Trade-off: "12,500" meant as 12.5 t is refused too; the message names both readings (Type 12500 or 12.5).
 */
export function parseTonsInput(raw: string): TonsParse {
  const t = raw.trim()
  if (t === '') return { ok: true, value: null }
  const th = /^(\d+),(\d{3})$/.exec(t)
  if (th) {
    const [, a, b] = th
    return { ok: false, kind: 'thousands', message: `Type ${(a + b).replace(/^0+(?=\d)/, '')} or ${a}.${b.replace(/0+$/, '') || '0'}` }
  }
  if ((t.match(/[.,]/g) ?? []).length > 1) {
    return { ok: false, kind: 'separators', message: 'Use one decimal separator, such as 12.5' }
  }
  const n = Number(t.replace(',', '.'))
  if (!/^(\d+[.,]?\d*|[.,]\d+)$/.test(t) || !Number.isFinite(n)) {
    return { ok: false, kind: 'nan', message: 'Type a number, such as 12.5' }
  }
  return { ok: true, value: n }
}

/**
 * Like safeDecimal but keeps a single leading minus. Use for values that can be
 * sub-zero (temperatures — reefer setpoints, chilled pulp). safeDecimal strips
 * the sign because money/qty/kg are never negative; this variant is the opt-in.
 */
export function safeSignedDecimal(v: string): string {
  const neg = v.trimStart().startsWith('-')
  return (neg ? '-' : '') + safeDecimal(v)
}

/**
 * Global guard for legacy `<input type="number">` call sites: a mouse-wheel
 * scroll while a number input is focused silently steps its value by ±1 with no
 * visual cue (KF49 2026-05-13: "17 bins" saved as 16). Blur the focused number
 * input on any wheel event so the page scrolls and the value stays put. Capture
 * phase so it runs before the input's own scroll handler. New inputs should use
 * the type="text" + safeDigits/safeDecimal pattern instead; this covers every
 * existing type="number" site in one shot. Called once at boot from main.tsx;
 * extracted here so the guard is unit-testable (REL-054).
 */
export function installNumberInputWheelGuard(): void {
  if (typeof document === 'undefined') return
  document.addEventListener('wheel', () => {
    const el = document.activeElement as HTMLInputElement | null
    if (el && el.tagName === 'INPUT' && el.type === 'number') {
      el.blur()
    }
  }, { passive: true, capture: true })
}
